<template>
  <div class="p-5 pt-3">
    <pop-up
      :active.sync="popUp"
      @close="$router.push({ name: 'SalesPlanList' })"
      @ok="$router.push({ name: 'SalesPlanList' })"
    >
      You have successfully uploaded sales plan
    </pop-up>
    <div
      v-show="isUserCanAccessMenu(permission.name, 'add')"
      class="bg-white rounded-2xl w-content py-4 px-5"
    >
      <div class="flex flex-wrap flex-row items-center justify-start">
        <span class="font-semibold">Type</span>
        <radio-button
          class="ml-8"
          v-model="type"
          vs-name="annual"
          vs-value="annual"
        >
          Annual
        </radio-button>
        <radio-button
          class="ml-4"
          v-model="type"
          vs-name="monthly"
          vs-value="monthly"
        >
          Monthly
        </radio-button>
      </div>
    </div>
    <upload-file
      v-show="isUserCanAccessMenu(permission.name, 'add')"
      class="mt-8"
      @upload="uploadFile"
      @preview="previewTable"
      @remove="removeFile"
    />
    <div
      v-if="!isEmpty(tableData)"
      class="mt-8 mr-8 flex"
    >
      <vs-button
        :disabled="isSubmitDisabled"
        class="submit-button"
        color="secondary"
        @click="submit"
      >
        Submit
      </vs-button>
    </div>
    <data-table
      ref="table"
      class="mt-8"
      :loading="isLoading"
      :table-header="tableHeaders"
      :table-data="transformData(tableData)"
      :sort-by="sortBy"
      :sort-dir="sortDir"
      @sort="sort"
    >
      <template #data="{ data, index }">
        <td
          class="td vs-table--td"
          :key="key"
          v-for="key in tableHeaders.reduce((acc,curr) => [...acc, curr.key], [])"
          :class="{
            'row-primary': data[index].port.includes('Total'),
            'row-secondary': data[index].port.includes('Grand Total')
          }"
        >
          <span>
            <div
              v-if="data[index].port.includes('Total')"
              :class="{
                'whitespace-no-wrap': true,
                'invisible': key === 'mine' || key === 'source'
              }"
            >
              {{ number(data[index][key]) }}
            </div>
            <div
              v-else
              :class="{
                'whitespace-no-wrap': true,
                'invisible': isInvisible(index, key, data),
                'text-secondary font-semibold italic': key === 'mine',
                'data data-error': isTonaseNumber(data[index][key], key)
              }"
            >
              {{ number(data[index][key]) }}
            </div>
          </span>
        </td>
      </template>
    </data-table>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import DataTable from '@/components/dataTable'
import PopUp from '@/components/popUp'
import RadioButton from '@/components/radioButton'
import UploadFile from '@/components/uploadFile'
import { PAGE_TITLE } from '@/fixtures/Constants'
import { isNumber, sortData,transformData } from '@/utils/salesPlanUtils'

export default {
  metaInfo: {
    title: `${PAGE_TITLE} Sales Plan Upload`
  },
  components: {
    RadioButton,
    UploadFile,
    DataTable,
    PopUp
  },
  data(){
    return {
      isLoading: false,
      isSubmitDisabled: false,
      type: null,
      sortBy: '',
      sortDir: null,
      tableHeaders: [
        { key: 'port', label: 'Port', sortable: true },
        { key: 'mine', label: 'Mine' },
        { key: 'source', label: 'Source' },
        { key: 'jan', label: 'Jan' },
        { key: 'feb', label: 'Feb' },
        { key: 'mar', label: 'Mar' },
        { key: 'apr', label: 'Apr' },
        { key: 'may', label: 'May' },
        { key: 'jun', label: 'Jun' },
        { key: 'jul', label: 'Jul' },
        { key: 'aug', label: 'Aug' },
        { key: 'sep', label: 'Sep' },
        { key: 'oct', label: 'Oct' },
        { key: 'nov', label: 'Nov' },
        { key: 'dec', label: 'Dec' },
        { key: 'grand_total', label: 'Grand Total' }
      ],
      tableData: [],
      popUp: false
    }
  },
  computed: {
    ...mapGetters({
      salesPlanPreview: 'SalesPlan/salesPlanPreview'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    }
  },
  methods: {
    transformData,
    isInvisible(index, key, data) {
      if (index && (key === 'port' || key === 'mine')) {
        return data[index][key] === data[index-1][key]
      }
      return false
    },
    isTonaseNumber(number, key) {
      if ((key !== 'port' && key !== 'mine' && key !== 'source' && key !== 'grand_total')
        && !isNumber(number)) {
        this.isSubmitDisabled = true
        return true
      }
      return false
    },
    async uploadFile(file) {
      try {
        await this.$store.dispatch('SalesPlan/uploadExcel', file)
      } catch (e) {
        this.$store.dispatch('UploadFile/reset')
        this.$vs.notify({
          title: 'Failed',
          text: 'Something went wrong',
          color: 'danger',
          icon: 'close',
          position: 'top-right'
        })
      }
    },
    previewTable() {
      this.isLoading = true
      this.tableData = this.salesPlanPreview.data
      this.isLoading = false
    },
    removeFile() {
      this.tableData = []
      this.isSubmitDisabled = false
    },
    sort(sortBy, sortDir) {
      this.isLoading = true
      this.sortBy = sortBy
      this.sortDir = sortDir

      this.tableData = !sortDir
        ? this.salesPlanPreview.data
        : sortData(this.salesPlanPreview.data, sortBy, sortDir)
      this.isLoading = false
    },
    number(number) {
      return isNumber(number)
        ? number.toLocaleString('id-ID')
        : number
    },
    async submit() {
      try {
        let form = {}
        if (this.type === 'annual') {
          form = {
            type: this.type,
            ...this.salesPlanPreview
          }
        } else {
          form = {
            type: this.type,
            month: this.moment().month()+1,
            ...this.salesPlanPreview
          }
          delete form.revision
        }

        await this.$store.dispatch('SalesPlan/submitSalesPlan', form)
        this.popUp = true
      } catch (e) {
        this.$vs.notify({
          title: 'Failed',
          text: 'Something went wrong',
          color: 'danger',
          icon: 'close',
          position: 'top-right'
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.submit-button {
  @apply rounded-xl;
  @apply py-4 #{!important};
  @apply px-16;
  @apply ml-auto;
  -webkit-box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary),1);
  box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary),1);

  &:disabled {
    @apply text-grey-001;
    background-color: #ECEBED !important;
    -webkit-box-shadow: none;
    box-shadow: none;
  }

  &:focus {
    @apply outline-none;
    @apply border-none;
  }
}
</style>
